import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withSequence, withTiming } from 'react-native-reanimated';

import { useThemedStyles } from '../../../hooks/useThemedStyles';

/**
 * A small vending-machine glyph in the My Day header that fills with product in
 * proportion to the driver's day-level progress (machines done vs assigned). It
 * stays visible across every board state, pops once when the day completes, and
 * is the parent's responsibility to hide when there are no assigned stops or in
 * training mode. The completion flourish is remembered per day via AsyncStorage
 * (device-local, like My Day step skips) so it does not replay on refresh or
 * when returning to the board.
 */
const MachineFillAvatar = ({
	ratio,
	isComplete,
	flourishStorageKey,
	accessibilityLabel,
}) => {
	const styles = useThemedStyles(createStyles);

	const fill = Math.min(1, Math.max(0, Number.isFinite(ratio) ? ratio : 0));
	const isFull = fill >= 1;

	const fillValue = useSharedValue(fill);
	const popValue = useSharedValue(1);

	const [storageReady, setStorageReady] = useState(false);
	const [hasFlourished, setHasFlourished] = useState(false);

	const fillStyle = useAnimatedStyle(() => ({
		height: `${fillValue.value * 100}%`,
	}));
	const popStyle = useAnimatedStyle(() => ({
		transform: [{ scale: popValue.value }],
	}));

	useEffect(() => {
		fillValue.value = withTiming(fill, { duration: 600 });
	}, [fill, fillValue]);

	// Remember whether today's completion flourish already played. Without a
	// storage key the avatar should never flourish; treat it as already shown.
	useEffect(() => {
		if (!flourishStorageKey) {
			setHasFlourished(true);
			setStorageReady(true);
			return undefined;
		}

		let isActive = true;
		setStorageReady(false);

		(async () => {
			let storedValue = null;
			try {
				storedValue = await AsyncStorage.getItem(flourishStorageKey);
			}
			catch (error) {
				// If storage is unreadable, do not risk replaying the flourish.
				storedValue = 'true';
			}
			if (isActive) {
				setHasFlourished(storedValue === 'true');
				setStorageReady(true);
			}
		})();

		return () => {
			isActive = false;
		};
	}, [flourishStorageKey]);

	useEffect(() => {
		if (!storageReady || !isComplete || hasFlourished) {
			return;
		}

		setHasFlourished(true);
		if (flourishStorageKey) {
			void AsyncStorage.setItem(flourishStorageKey, 'true');
		}
		popValue.value = withSequence(
			withTiming(1.18, { duration: 160 }),
			withTiming(1, { duration: 260 }),
		);
	}, [flourishStorageKey, hasFlourished, isComplete, popValue, storageReady]);

	return (
		<Animated.View
			style={[styles.wrap, popStyle]}
			accessibilityRole="image"
			accessibilityLabel={accessibilityLabel}
		>
			<View style={styles.cabinet}>
				<View style={styles.glass}>
					<View style={[styles.shelf, styles.shelf1]} />
					<View style={[styles.shelf, styles.shelf2]} />
					<View style={[styles.shelf, styles.shelf3]} />
					<Animated.View style={[styles.fill, isFull && styles.fillFull, fillStyle]} />
				</View>
				<View style={styles.dispenseSlot} />
			</View>
		</Animated.View>
	);
};

const createStyles = (theme) => ({
	// Sized to sit within the two-line "My day · route/date" heading so it never
	// grows the header card or pushes the board content down.
	wrap: {
		flexShrink: 0,
		marginRight: 10,
		alignSelf: 'center',
	},
	cabinet: {
		width: 24,
		height: 44,
		borderWidth: 2,
		borderColor: theme.text2,
		borderRadius: 5,
		backgroundColor: theme.mainBg1,
		padding: 2,
	},
	glass: {
		flex: 1,
		borderRadius: 3,
		backgroundColor: theme.mainBg3,
		overflow: 'hidden',
		position: 'relative',
	},
	shelf: {
		position: 'absolute',
		left: 0,
		right: 0,
		height: 1,
		backgroundColor: theme.border3,
	},
	shelf1: {
		top: '25%',
	},
	shelf2: {
		top: '50%',
	},
	shelf3: {
		top: '75%',
	},
	fill: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: theme.primary1,
	},
	fillFull: {
		backgroundColor: theme.success1,
	},
	dispenseSlot: {
		height: 3,
		marginHorizontal: 4,
		marginTop: 2,
		borderRadius: 2,
		backgroundColor: theme.text2,
	},
});

export default MachineFillAvatar;
